// Copyright: IQGeo Limited 2010-2019
import _ from 'underscore';
import L from 'leaflet';
import config from 'myWorld/base/config';
import myw from 'myWorld/base/core';
import 'myWorld/base/util';
import 'myWorld/base/svgRenderer';
import './mywIcon';

myw.StyleManager = myw.MywClass.extend({
    /** List of dash names mapped to dash-arrays. We use the VML names and create SVG dash arrays that correspond to these.
     * For browsers using VML such as IE8, we pass the name in directly; for browsers using SVG we use the array of integer values.
     *
     *  SVG -  https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/stroke-dasharray
     *  VML - http://msdn.microsoft.com/en-us/library/bb264085%28v=vs.85%29.aspx
     *
     */
    dashStyles: {
        dash: [5, 5],
        shortdash: [2, 2],
        longdash: [10, 4],
        dot: [1, 2],
        longdashdot: [10, 5, 1, 5]
    },

    /** List of valid symbol names.
     */
    symbol_names: Object.keys(myw.svgRenderer.symbols),

    /** Default point style if no style has been configured
     */
    defaultPointStyle: '',

    /** Default line style if no style has been configured
     */
    defaultLineStyle: 'green:2',

    /** Default fill style if no style has been configured
     */
    defaultFillStyle: 'green:40',

    /**
     * @class Manages style for features on a vector layer. Main function is to convert the style strings into options objects
     *   to be used by {@link FeatureRepresentation}
     * @extends {MywClass}
     * @constructs
     */
    initialize() {
        const defaultMarkerStyleHighlight = myw.Util.fixIconDefPath(
            config['core.defaultMarkerStyleHighlight']
        );
        this.defaultHighlightMarker = { icon: new L.Icon(defaultMarkerStyleHighlight) };
    },

    /**
     * Return style information for a feature.
     * @param  {Feature} Feature to return style for
     * @param  {LayerFeatureItem} styleDef  Describes styling for this feature in layer
     * @return {styleDefinition}  Style options for feature or undefined if there are none
     */
    getStyleFor(feature, styleDef) {
        const styles = {},
            geometryType = feature.getDDGeometryType();

        const layerOpacity = styleDef.opacity;
        const minZoom = styleDef.min_vis;
        const maxZoom = styleDef.max_vis;

        styles.text = this.getTextStyle(styleDef.text_style, minZoom, maxZoom);

        switch (geometryType) {
            case 'Point':
            case 'MultiPoint':
                styles.normal = this.getPointStyle(
                    styleDef.point_style || this.defaultPointStyle,
                    layerOpacity,
                    feature
                );
                styles.highlight = styleDef.point_style_highlight
                    ? this.getPointStyle(styleDef.point_style_highlight, layerOpacity, feature)
                    : this.defaultHighlightMarker;
                break;
            case 'LineString':
            case 'MultiLineString':
                styles.normal = this.getLineStyle(
                    styleDef.line_style || this.defaultLineStyle,
                    layerOpacity
                );
                styles.highlight = config['core.defaultPolylineStyleHighlight'];
                break;
            case 'Polygon':
            case 'MultiPolygon':
                styles.normal = this.getPolygonStyle(
                    styleDef.line_style || this.defaultLineStyle,
                    styleDef.fill_style || this.defaultFillStyle,
                    layerOpacity
                );
                styles.highlight = config['core.defaultPolygonStyleHighlight'];
                break;
        }
        return styles;
    },

    /**
     * Create an icon marker for point feature
     * @param  {string} pointStyleString Marker icon options that contains icon url or json string with icon settings
     * @return {pointStyle}                   Style options for marker
     */
    getPointStyle(pointStyleString, opacity, feature) {
        let styleObj, style;

        if (!pointStyleString) {
            return undefined;
        }

        const styleData = pointStyleString.split(':');

        if (_.contains(this.symbol_names, styleData[0])) {
            style = {
                symbol: {
                    name: styleData[0],
                    color: styleData[1],
                    size: styleData[2],
                    borderColor: styleData[3] || styleData[1],
                    opacity: opacity
                }
            };
        } else if (pointStyleString.charAt(0) == '{') {
            // test to see if string will evaluate to json
            try {
                styleObj = JSON.parse(pointStyleString);
                styleObj = myw.Util.fixIconDefPath(styleObj);
            } catch (e) {
                // if not, let the user know what's wrong
                console.log(
                    `Problem with a point style configuration for ${pointStyleString} with an error of: ${e}`
                );
                return;
            }

            style = { icon: new L.Icon(styleObj) };
        } else {
            // Default values picked for backwards compatibility.

            const iconUrl = styleData[0];

            let ax = 16;
            let ay = 32;

            if (styleData.length > 1) {
                ax = styleData[1];
                ay = styleData[2];
            }

            let iconDef = { iconUrl: iconUrl, iconAnchor: [ax, ay] };
            iconDef = myw.Util.fixIconDefPath(iconDef);
            const geometryFieldName = feature.getGeometryFieldNameInWorld('geo');
            const icon = new myw.MywIcon({
                ...iconDef,
                width: styleData[3],
                feature,
                orientation: feature.properties[`myw_orientation_${geometryFieldName}`] || 0
            });
            style = { icon: icon };
        }

        return style;
    },

    /**
     * Create line style option object for leaflet vector polylines
     * @param  {string} lineStyleString a ':' delimited string with color and width info ie: 'red:5'
     * @return {lineStyle}
     */
    getLineStyle(lineStyleString, opacity) {
        if (!lineStyleString) {
            lineStyleString = this.defaultLineStyle;
        }

        const normalStyle = lineStyleString.split(':');
        let normal = {};

        // Normal style
        normal = {};
        normal.color = normalStyle[0];
        const size = normalStyle[1];
        if (size[size.length - 1] == 'm') {
            normal.weightUnit = 'm';
            normal.weight = parseFloat(size.substring(0, size.length - 1));
        } else {
            normal.weightUnit = 'px';
            normal.weight = parseFloat(size);
        }
        normal.opacity = opacity || 1;

        if (normalStyle.length > 2) {
            if (normalStyle[2] != 'solid') {
                normal.dashArray = this.parseDashStyle(normalStyle[2], normal.weight);
            }
        }

        if (normalStyle.length > 3) normal.start_arrow = normalStyle[3];
        else normal.start_arrow = 'none';

        if (normalStyle.length > 4) normal.end_arrow = normalStyle[4];
        else normal.end_arrow = 'none';

        return normal;
    },

    /**
     * Returns text style options
     * @param  {string} textStyleString [description]
     * @param  {integer} [minZoom] Default min zoom to be used if style doesn't specify one
     * @param  {integer} [maxZoom] Default max zoom to be used if style doesn't specify one
     * @return {textStyle}
     */
    getTextStyle(textStyleString, minZoom, maxZoom) {
        if (!textStyleString) {
            return null;
        }

        const styleData = textStyleString.split(':');
        const fieldName = styleData[0];
        const fontColor = styleData[1];
        const fontFamily = 'Arial';
        let fontSize = styleData[2];
        const fontStyle = 'normal';
        let valign = styleData[3];
        let halign = styleData[4];
        const bcolor = styleData[5];
        const border = parseFloat(styleData[6]);
        const min_vis = parseInt(styleData[7]) || minZoom || '';
        const max_vis = parseInt(styleData[8]) || maxZoom || '';

        let worldSize = false;

        if (fontSize[fontSize.length - 1] == 'm') {
            worldSize = true;
            fontSize = fontSize.substring(0, fontSize.length - 1);
        }
        fontSize = parseFloat(fontSize);

        // Extract the vertical and horizontal offsets from the anchor
        let vOffset = 0,
            hOffset = 0,
            vs = [],
            hs = [];

        if (valign.indexOf('+') >= 0) {
            vs = valign.split('+');
            valign = vs[0];
            vOffset = vs[1];
        } else if (valign.indexOf('-') >= 0) {
            vs = valign.split('-');
            valign = vs[0];
            vOffset = -vs[1];
        }

        if (halign.indexOf('+') >= 0) {
            hs = halign.split('+');
            halign = hs[0];
            hOffset = hs[1];
        } else if (halign.indexOf('-') >= 0) {
            hs = halign.split('-');
            halign = hs[0];
            hOffset = -hs[1];
        }

        return {
            fontColor: fontColor,
            fontSize: fontSize,
            fontFamily: fontFamily,
            fontStyle: fontStyle,
            valign: valign,
            halign: halign,
            vOffset: vOffset,
            hOffset: hOffset,
            bcolor: bcolor,
            fieldName: fieldName,
            border: border,
            worldSize: worldSize,
            min_vis: min_vis,
            max_vis: max_vis
        };
    },

    /**
     * Convert named dash style into a dashArray if using SVG otherwise just return dashName.
     * @param  {string}     dashName
     * @param  {integer}    lineWeight
     * @return {integer[]|string}  A dashArray corresponding to supplied dashName or a string
     */
    parseDashStyle(dashName, lineWeight) {
        dashName = dashName.toLowerCase();
        const da = this.dashStyles[dashName];
        if (da) {
            if (L.Browser.vml) {
                return dashName;
            } else {
                // Scale the dash array lengths according to line weight
                return _.map(da, d => d * lineWeight);
            }
        } else {
            console.log(
                `Problem with a line style configuration. Unknown dash style name '${dashName}'`
            );
        }
    },

    /**
     * Create polygon fill option object for leaflet vector polygons. We allow the possibility of either one of the
     * line or fill styles being blank.
     * @param  {string} lineStyleString a ':' delimited string with color and width info ie: 'red:5'
     * @param  {string} fillStyleString a ':' delimited string with color and opacity as a percent info ie: 'red:75'
     * @return {polygonStyle}
     */
    getPolygonStyle(lineStyleString, fillStyleString, layerOpacity) {
        const normal = {};
        let fillOpacity;

        if (!(lineStyleString || fillStyleString)) {
            lineStyleString = this.defaultLineStyle;
            fillStyleString = this.defaultFillStyle;
        }

        if (lineStyleString) {
            const lineStyle = lineStyleString.split(':');

            // outline style
            normal.color = lineStyle[0];
            normal.weight = parseFloat(lineStyle[1]);
            normal.opacity = layerOpacity || 1;

            if (lineStyle.length > 2) {
                if (lineStyle[2] != 'solid') {
                    normal.dashArray = this.parseDashStyle(lineStyle[2], lineStyle[1]);
                }
            }
        } else {
            normal.stroke = false;
        }

        if (fillStyleString) {
            const fillStyle = fillStyleString.split(':');

            normal.fill = true;
            normal.fillColor = fillStyle[0];
            fillOpacity = this.getOpacity(fillStyle[1]);
            // fillOpacity is configured in style picker in config page whereas layerOpacity is on layer def.
            normal.fillOpacity = fillOpacity * layerOpacity;
        } else {
            normal.fill = false;
        }
        return normal;
    },
    /**
     * convert opacity number to float for leaflet: 75 --> 0.75
     * @param  {integer} opacityInteger
     * @return {float}
     */
    getOpacity(opacityInteger) {
        let opacity = parseFloat(opacityInteger);

        opacity = opacity / 100;

        return opacity;
    }
});

/**
 * Style definition
 * @typedef styleDefinition
 * @property {pointStyle|pathOptions}    normal      Style to use by default
 * @property {pointStyle|pathOptions}    highlight   Style to use when the feature is highlighted
 */

/**
 * Point style definition.
 * @typedef pointStyle
 * @property {L.Icon} icon  Marker to display for the point. Refer to {@link http://leafletjs.com/reference.html#icon L.Icon}
 * @property  {symbolStyle} symbol Symbol to display for the point.
 */

/**
 * Symbol style definition
 * @typedef symbolStyle
 * @property {string} name Name of symbol. One of 'circle', 'square', 'triangle', 'cross' or 'x'
 * @property {string} color Colour of symbol
 * @property {string} size Size of symbol
 */

/**
 * Line style definition
 * @typedef lineStyle
 * @property {string} color Colour of line
 * @property {string} weight Weight/thickness of line
 * @property {string} start_arrow  'none' or 'arrow' to indicate an arrow is not or is to be drawn at start of line
 * @property {string} send_arrow 'none' or 'arrow' to indicate an arrow is not or is to be drawn at end of line
 * @property {string} dashArray Description of how the line is to be dashed
 */

/**
 * Leaflet Path Options. Amongst other things, controls line width & colour and polygon fill & apacity Refer to {@link http://leafletjs.com/reference.html#path-options L.Path-options}
 * @typedef pathOptions
 */

export default myw.StyleManager;
